import { atom, createStore } from 'jotai'
import {
  EmitterEvent,
  EmitterEventTypes,
  EmitterGlobalSubscription,
  EmitterPayloadFromEventType,
  EmitterSubscription,
  EmitterUnsubscribe,
} from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'

interface EmitterServiceControl {
  subscribe: <TEventType extends EmitterEventTypes>(
    subscription: EmitterSubscription<TEventType>,
  ) => EmitterUnsubscribe
  subscribeAll: (subscription: EmitterGlobalSubscription) => EmitterUnsubscribe
  emit: <TEventType extends EmitterEventTypes>(
    eventType: TEventType,
    eventPayload: EmitterPayloadFromEventType<TEventType>,
  ) => void
  // reset the emitter service - just for testing
  reset: () => void
}

const subscriptions =
  atom<Map<EmitterEventTypes, EmitterEvent<EmitterEventTypes>[]>>()
const globalSubscriptions = atom<EmitterGlobalSubscription[]>([])
const eventStore = createStore()
eventStore.set(subscriptions, new Map())
eventStore.set(globalSubscriptions, [])
const logger = new Logger('EmitterService')

const subscribe = <TEventType extends EmitterEventTypes>(
  newSub: EmitterSubscription<TEventType>,
): EmitterUnsubscribe => {
  const { eventType, callback } = newSub
  const subs = eventStore.get(subscriptions) ?? new Map()
  const eventSubs = subs.get(eventType) ?? []
  const newSubs = new Map(subs)
  newSubs.set(eventType, [...eventSubs, callback as EmitterEvent<TEventType>])
  eventStore.set(subscriptions, newSubs)

  return () => {
    const currSubs = eventStore.get(subscriptions) ?? new Map()
    const currEventSubs = currSubs.get(eventType) ?? []
    const newMap = new Map(currSubs)

    const indexToRemove = currEventSubs.indexOf(
      callback as EmitterEvent<TEventType>,
    )
    if (indexToRemove >= 0) {
      const newEventSubs = [...currEventSubs]
      newEventSubs.splice(indexToRemove, 1)
      newMap.set(eventType, newEventSubs)
      eventStore.set(subscriptions, newMap)
    }
  }
}

const subscribeAll = (
  newSub: EmitterGlobalSubscription,
): EmitterUnsubscribe => {
  const subs = eventStore.get(globalSubscriptions) ?? []
  const newGlobalSubs = [...subs, newSub]
  eventStore.set(globalSubscriptions, newGlobalSubs)

  return () => {
    const currSubs = eventStore.get(globalSubscriptions) ?? []
    const newSubs = currSubs.filter(
      (sub: EmitterGlobalSubscription) => sub !== newSub,
    )
    eventStore.set(globalSubscriptions, newSubs)
  }
}

const emit = <TEventType extends EmitterEventTypes>(
  eventType: TEventType,
  eventPayload: EmitterPayloadFromEventType<TEventType>,
): void => {
  const subs = eventStore.get(subscriptions) ?? new Map()
  const eventSubscriptions = subs.get(eventType) ?? []
  eventSubscriptions.forEach((callback: EmitterEvent<TEventType>) => {
    try {
      callback(eventPayload)
    } catch (e: unknown) {
      logger.error(`Emit Error in event callback: ${e}`)
    }
  })

  const globalSubs = eventStore.get(globalSubscriptions) ?? []
  globalSubs.forEach((globalSub: EmitterGlobalSubscription) => {
    try {
      globalSub.callback(eventType, eventPayload)
    } catch (e: unknown) {
      logger.error(`Emit Error in global event callback: ${e}`)
    }
  })
}

// reset the emitter service - just for testing
const reset = (): void => {
  eventStore.set(subscriptions, new Map())
  eventStore.set(globalSubscriptions, [])
}

const EmitterService = {
  subscribe,
  subscribeAll,
  emit,
  reset,
}

export default EmitterService as EmitterServiceControl
